// @ts-nocheck
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { SceneProps } from './sceneTypes';
import { rgb } from './sceneTypes';

export function moodProfile(style: SceneProps['visualStyle']) {
  switch (style) {
    case 'none':
      return { density: 1, speed: 0.88, flash: 0.55, depth: 1, lyric: 1, geometry: 1, softness: 1 };
    case 'psychedelic':
      return { density: 1.35, speed: 1.16, flash: 1.05, depth: 1.08, lyric: 1.18, geometry: 1.45, softness: 0.85 };
    case 'plasma':
      return { density: 1.18, speed: 0.92, flash: 0.86, depth: 1.18, lyric: 1.05, geometry: 1.28, softness: 1.12 };
    case 'vortex':
      return { density: 1.28, speed: 1.12, flash: 0.92, depth: 1.5, lyric: 0.96, geometry: 1.32, softness: 0.95 };
    case 'strobe-burst':
      return { density: 1.36, speed: 1.22, flash: 1.38, depth: 1.05, lyric: 1.0, geometry: 1.18, softness: 0.66 };
    case 'flash':
      return { density: 0.74, speed: 0.82, flash: 1.65, depth: 0.82, lyric: 0.88, geometry: 0.82, softness: 0.58 };
    case 'neon-journey':
      return { density: 1.16, speed: 1.02, flash: 0.92, depth: 1.34, lyric: 1.0, geometry: 1.08, softness: 0.86 };
    case 'cinematic':
      return { density: 0.84, speed: 0.72, flash: 0.48, depth: 1.42, lyric: 0.92, geometry: 0.95, softness: 1.5 };
    case 'minimal':
      return { density: 0.55, speed: 0.62, flash: 0.34, depth: 0.92, lyric: 0.8, geometry: 0.72, softness: 1.15 };
    case 'emotional':
      return { density: 0.82, speed: 0.78, flash: 0.55, depth: 1.12, lyric: 1.45, geometry: 0.9, softness: 1.75 };
    case 'horizon-journey':
      return { density: 0.94, speed: 0.88, flash: 0.68, depth: 1.28, lyric: 1.04, geometry: 0.98, softness: 1.18 };
    case 'aggressive':
      return { density: 1.45, speed: 1.42, flash: 1.7, depth: 1.12, lyric: 1.1, geometry: 1.18, softness: 0.55 };
    case 'cyberpunk':
      return { density: 1.18, speed: 1.08, flash: 1.0, depth: 1.2, lyric: 0.95, geometry: 1.12, softness: 0.72 };
    case 'space':
      return { density: 1.1, speed: 0.82, flash: 0.62, depth: 1.72, lyric: 0.92, geometry: 1.05, softness: 1.25 };
    default:
      return { density: 1, speed: 1, flash: 1, depth: 1, lyric: 1, geometry: 1, softness: 1 };
  }
}

export function sectionBoost(section: SceneProps['section']) {
  switch (section) {
    case 'intro': return { energy: 0.65, contrast: 0.72 };
    case 'verse': return { energy: 0.78, contrast: 0.82 };
    case 'build': return { energy: 1.12, contrast: 1.05 };
    case 'chorus': return { energy: 1.25, contrast: 1.12 };
    case 'drop': return { energy: 1.55, contrast: 1.35 };
    case 'bridge': return { energy: 0.86, contrast: 0.95 };
    case 'outro': return { energy: 0.58, contrast: 0.65 };
    default: return { energy: 1, contrast: 1 };
  }
}

export function sceneTuning(props: SceneProps) {
  const mood = moodProfile(props.visualStyle);
  const section = sectionBoost(props.section);
  const tempoScale = props.visualStyle === 'aggressive'
    ? 0.62
    : props.visualStyle === 'festival'
      || props.visualStyle === 'psychedelic'
      || props.visualStyle === 'strobe-burst'
      || props.visualStyle === 'vortex'
      ? 0.58
      : 0.52;
  return {
    ...mood,
    speed: mood.speed * tempoScale,
    energy: section.energy,
    contrast: section.contrast,
    effectiveIntensity: props.intensity * section.energy,
  };
}

export function sceneJourney(props: SceneProps) {
  const durationMs = Math.max(1, props.eventEndMs - props.eventStartMs);
  const elapsedMs = Math.max(0, props.progressMs - props.eventStartMs);
  const progress = Math.min(1, elapsedMs / durationMs);
  const wave = Math.sin(progress * Math.PI);
  const chapter = Math.min(3, Math.floor(progress * 4));
  const chapterProgress = Math.min(1, (progress * 4) % 1);
  const reveal = Math.min(1, progress / 0.18);
  const peak = Math.max(0, 1 - Math.abs(progress - 0.68) / 0.2);
  const release = Math.max(0, (progress - 0.78) / 0.22);
  const turn = progress * Math.PI * 2;

  return {
    durationMs,
    elapsedMs,
    progress,
    chapter,
    chapterProgress,
    reveal,
    wave,
    peak,
    release,
    turn,
    chapterName: ['reveal', 'expand', 'peak', 'release'][chapter],
  };
}

export function colorVector(color: [number, number, number], gain = 1) {
  return new THREE.Color(
    (color[0] / 255) * gain,
    (color[1] / 255) * gain,
    (color[2] / 255) * gain
  );
}

export function musicalImpact(props: SceneProps, intervalOverride?: number) {
  const { beat, section } = props;
  const interval = intervalOverride
    ?? (section === 'drop' || section === 'build' || section === 'chorus' ? 8 : 16);
  const window = section === 'drop' ? 1.35 : section === 'build' || section === 'chorus' ? 1.15 : 0.95;
  const ageBeats = (beat.beatIndex % interval) + beat.beatPhase;
  return ageBeats <= window ? Math.pow(1 - ageBeats / window, 1.45) : 0;
}

export function musicalPulse(props: SceneProps) {
  const { beat, intensity } = props;
  const groove = Math.pow(Math.max(0, 1 - beat.beatPhase), 1.25) * intensity * 0.22;
  return {
    groove,
    impact: musicalImpact(props) * intensity,
    phrase: musicalImpact(props, 32) * intensity,
  };
}

export function BeatFlash(props: SceneProps) {
  const { palette } = props;
  const tune = sceneTuning(props);
  const { impact, phrase } = musicalPulse(props);
  const opacity = Math.max(0, (impact * 0.16 + phrase * 0.14) * tune.flash * tune.contrast);
  if (opacity < 0.02) return null;
  return (
    <mesh position={[0, 0, -3.4]}>
      <planeGeometry args={[18, 10]} />
      <meshBasicMaterial
        color={rgb(palette.glow)}
        transparent
        opacity={opacity}
        depthWrite={false}
      />
    </mesh>
  );
}

export function SceneVignette(props: SceneProps) {
  const { palette, intensity, visualStyle } = props;
  const tune = sceneTuning(props);
  const haze = visualStyle === 'cinematic' || visualStyle === 'emotional' ? palette.primary : palette.background;
  return (
    <group>
      <mesh position={[0, 0, -8]}>
        <planeGeometry args={[60, 36]} />
        <meshBasicMaterial
          color={rgb(haze)}
          transparent
          opacity={(0.24 + intensity * 0.08) * tune.softness}
          depthWrite={false}
        />
      </mesh>
      <mesh position={[0, -3.8, -2.5]} rotation={[-Math.PI / 2.2, 0, 0]}>
        <circleGeometry args={[7.5, 96]} />
        <meshBasicMaterial
          color={rgb(palette.glow)}
          transparent
          opacity={(0.06 + intensity * 0.07) * tune.depth}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

export function BeatRings(props: SceneProps) {
  const { palette, beat, intensity, section } = props;
  const tune = sceneTuning(props);
  const group = useRef<THREE.Group>(null);
  const impactInterval = section === 'drop' || section === 'build' || section === 'chorus' ? 8 : 16;
  const impactAgeBeats = (beat.beatIndex % impactInterval) + beat.beatPhase;
  const impactWindow = section === 'drop' ? 1.35 : 1.15;
  const impactEnvelope = musicalImpact(props, impactInterval);
  const count = impactEnvelope > 0.01 ? 2 : 0;
  const rings = useMemo(() => Array.from({ length: count }, (_, i) => i), [count]);

  useFrame((state) => {
    if (!group.current) return;
    group.current.rotation.z = state.clock.elapsedTime * (0.08 + intensity * 0.08) * tune.speed;
  });

  if (impactEnvelope <= 0.01) return null;

  return (
    <group ref={group} position={[0, 0, -1.2]}>
      {rings.map((i) => {
        const phase = Math.min(1, impactAgeBeats / impactWindow + i * 0.18);
        const radius = 0.9 + phase * (4.6 + intensity * tune.depth);
        const opacity = Math.max(0, impactEnvelope * (0.07 + intensity * 0.08) * tune.flash * (i === 0 ? 1 : 0.55));
        return (
          <mesh key={i}>
            <torusGeometry args={[radius, 0.01 + intensity * 0.004, 6, 120]} />
            <meshBasicMaterial
              color={rgb(i % 2 ? palette.primary : palette.secondary)}
              transparent
              opacity={opacity}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        );
      })}
    </group>
  );
}

export function ConnectionLine({
  from,
  to,
  color,
  opacity = 0.2,
}: {
  from: [number, number, number];
  to: [number, number, number];
  color: string;
  opacity?: number;
}) {
  const { midpoint, length, quaternion } = useMemo(() => {
    const a = new THREE.Vector3(...from);
    const b = new THREE.Vector3(...to);
    const mid = a.clone().add(b).multiplyScalar(0.5);
    const dir = b.clone().sub(a);
    const len = dir.length();
    const quat = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      dir.clone().normalize()
    );
    return { midpoint: mid, length: len, quaternion: quat };
  }, [from, to]);

  return (
    <mesh position={midpoint} quaternion={quaternion}>
      <cylinderGeometry args={[0.008, 0.008, length, 8]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} />
    </mesh>
  );
}

export function AlbumTile({
  texture,
  col,
  row,
  grid,
  size,
  tint,
  opacity,
}: {
  texture: THREE.Texture | null;
  col: number;
  row: number;
  grid: number;
  size: number;
  tint: THREE.Color;
  opacity: number;
}) {
  const geometry = useMemo(() => {
    const geo = new THREE.PlaneGeometry(size, size);
    const uv = geo.attributes.uv;
    const u0 = col / grid;
    const u1 = (col + 1) / grid;
    const v0 = 1 - (row + 1) / grid;
    const v1 = 1 - row / grid;
    uv.setXY(0, u0, v1);
    uv.setXY(1, u1, v1);
    uv.setXY(2, u0, v0);
    uv.setXY(3, u1, v0);
    uv.needsUpdate = true;
    return geo;
  }, [col, row, grid, size]);

  return (
    <mesh geometry={geometry}>
      {texture ? (
        <meshBasicMaterial map={texture} transparent opacity={opacity} color={tint} />
      ) : (
        <meshBasicMaterial color={tint} transparent opacity={opacity} />
      )}
    </mesh>
  );
}

export function seededUnit(seed: number, index: number, salt = 1) {
  const x = Math.sin(seed * 12.9898 + index * 78.233 + salt * 37.719) * 43758.5453;
  return x - Math.floor(x);
}
